import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { fmtKm, fmtMin, formatDate } from '../utils/mockState';
import { loadCostModel } from '../utils/costModel';
import { fetchRoadGeometry } from '../utils/osrm';
import api from '../api/client';
import { Vehicle, RouteEntity } from '../types';
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../utils/mapTiles';

declare const L: any;

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

function todayLabel(): string {
  const s = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function last14Days(): { key: string; day: string }[] {
  const days: { key: string; day: string }[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const day = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    days.push({ key, day });
  }
  return days;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [routes, setRoutes] = useState<RouteEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; data: any } | null>(null);
  const costModel = loadCostModel();

  useEffect(() => {
    Promise.all([api.get<Vehicle[]>('/vehicles'), api.get<RouteEntity[]>('/routes')])
      .then(([v, r]) => {
        setVehicles(v.data);
        setRoutes(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeVehicles = vehicles.filter((v) => v.active).length;
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayRoutes = useMemo(() => routes.filter((r) => (r.date || '').slice(0, 10) === todayKey), [routes, todayKey]);

  const allStops = useMemo(() => routes.flatMap((r) => r.stops || []), [routes]);
  const completedStops = allStops.filter((s) => s.status === 'concluida').length;
  const totalStops = allStops.length;

  const savingsRoutes = routes.filter((r) => r.savingsPercent > 0);
  const avgSavings = savingsRoutes.length
    ? savingsRoutes.reduce((s, r) => s + r.savingsPercent, 0) / savingsRoutes.length
    : 0;
  const totalKm = routes.reduce((s, r) => s + r.distanceM, 0) / 1000;
  const fuelLiters = totalKm * costModel.fuelConsumption;
  const moneySaved = avgSavings > 0 && avgSavings < 100
    ? fuelLiters * (avgSavings / (100 - avgSavings)) * costModel.fuelPrice
    : 0;

  // Chart: real completed-stop counts per day (last 14 days), by RouteStop.completedAt
  const chartDays = useMemo(() => {
    const days = last14Days();
    const counts = new Map(days.map((d) => [d.key, 0]));
    allStops.forEach((s) => {
      if (s.status === 'concluida' && s.completedAt) {
        const key = s.completedAt.slice(0, 10);
        if (counts.has(key)) counts.set(key, (counts.get(key) || 0) + 1);
      }
    });
    return days.map((d, i) => ({ day: d.day, val: counts.get(d.key) || 0, goal: 60, isToday: i === days.length - 1 }));
  }, [allStops]);
  const maxChartVal = Math.max(80, ...chartDays.map((d) => d.val));

  // Initialize mini Leaflet map
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;
    if (typeof L === 'undefined') return;

    try {
      if (mapRef.current) {
        mapRef.current.stop();
        mapRef.current.off();
        mapRef.current.remove();
        mapRef.current = null;
      }

      // The preview can be recreated whenever the dashboard refreshes. Prevent a
      // pending Leaflet transition from running after its map pane is removed.
      const map = L.map(container, {
        zoomControl: false,
        attributionControl: false,
        zoomAnimation: false,
        fadeAnimation: false,
        markerZoomAnimation: false,
        inertia: false
      });
      map.setView([-23.5615, -46.656], 11, { animate: false });

      L.tileLayer(TILE_URL, {
        maxZoom: 19
      }).addTo(map);

      const colors = ['#0f4c5c', '#2a9d8f', '#e76f51', '#e9c46a'];
      const bounds: any[] = [];
      let cancelled = false;

      todayRoutes.slice(0, 3).forEach((r, idx) => {
        const stops = r.stops || [];
        if (stops.length < 2) return;
        const points = stops.map((s) => ({ lat: s.deliveryPoint.latitude, lng: s.deliveryPoint.longitude }));
        const latlngs = points.map((p) => [p.lat, p.lng]);
        latlngs.forEach((ll) => bounds.push(ll));

        const poly = L.polyline(latlngs, {
          color: colors[idx % colors.length],
          weight: 4,
          opacity: 0.85
        }).addTo(map);

        fetchRoadGeometry(points).then((geometry) => {
          if (cancelled || mapRef.current !== map || !map._loaded || !geometry) return;
          poly.setLatLngs(geometry);
        });

        L.circleMarker(latlngs[0], {
          radius: 6,
          color: '#fff',
          weight: 2,
          fillColor: colors[idx % colors.length],
          fillOpacity: 1
        }).addTo(map);
      });

      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [20, 20], animate: false });
      }

      mapRef.current = map;

      return () => {
        cancelled = true;
        map.stop();
        map.off();
        map.remove();
        if (mapRef.current === map) {
          mapRef.current = null;
        }
      };
    } catch (err) {
      console.error("Erro ao inicializar mapa do dashboard:", err);
    }
  }, [todayRoutes]);

  if (loading) {
    return (
      <Layout>
        <div className="empty"><p>Carregando painel...</p></div>
      </Layout>
    );
  }

  const firstName = (me?.name || 'usuário').split(' ')[0];

  return (
    <Layout>
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting()}, {firstName}.</h1>
          <p className="page-lede">
            {todayLabel()} · {routes.length} rotas cadastradas · {activeVehicles} veículos operacionais
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/relatorios')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Exportar dia
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/rotas/nova')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Nova rota
          </button>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="stats-grid">
        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="6" cy="19" r="3"></circle>
                <circle cx="18" cy="5" r="3"></circle>
                <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-9a3.5 3.5 0 0 1 0-7H12"></path>
              </svg>
            </span>
            Rotas otimizadas hoje
          </div>
          <div className="val">{todayRoutes.length}</div>
          <div className="foot">{routes.length} rotas no total do sistema</div>
        </div>

        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13"></rect>
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                <circle cx="5.5" cy="18.5" r="2.5"></circle>
                <circle cx="18.5" cy="18.5" r="2.5"></circle>
              </svg>
            </span>
            Veículos operacionais
          </div>
          <div className="val">
            {activeVehicles}
            <span className="unit">/ {vehicles.length} frota</span>
          </div>
          <div className="delta flat">
            {vehicles.length > 0 ? Math.round((activeVehicles / vehicles.length) * 100) : 0}% ativo
          </div>
          <div className="foot">{vehicles.length - activeVehicles} veículo(s) em manutenção</div>
        </div>

        <div className="stat">
          <div className="lbl">
            <span className="ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </span>
            Entregas concluídas
          </div>
          <div className="val">
            {completedStops}
            <span className="unit">/ {totalStops} total</span>
          </div>
          <div className="delta up">
            {totalStops > 0 ? Math.round((completedStops / totalStops) * 100) : 0}% concluído
          </div>
          <div className="foot">{totalStops - completedStops} paradas restantes</div>
        </div>

        <div className="stat highlight" style={{ background: 'var(--teal-500-soft)', borderColor: 'var(--teal-500)' }}>
          <div className="lbl" style={{ color: 'var(--teal-800)' }}>
            <span className="ico" style={{ background: '#fff' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </span>
            Economia estimada
          </div>
          <div className="val" style={{ color: 'var(--teal-800)' }}>
            {avgSavings > 0 ? `${avgSavings.toFixed(1).replace('.', ',')}%` : '—'}
          </div>
          <div className="delta up" style={{ background: '#fff', color: '#0d7a6c' }}>
            {avgSavings > 0 ? `~R$ ${moneySaved.toFixed(2).replace('.', ',')}` : 'Sem otimizações ainda'}
          </div>
          <div className="foot" style={{ color: 'var(--teal-900)' }}>Redução direta de km rodado e combustível</div>
        </div>
      </div>

      {/* 2-Column Grid: SVG Chart & Mini Map */}
      <div className="dash-grid">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Volume de entregas — últimos 14 dias</div>
              <div className="card-sub">Entregas concluídas por dia (paradas com status concluída)</div>
            </div>
            <span className="badge b-teal">
              <span className="dot"></span>
              Operação estável
            </span>
          </div>
          <div className="card-body">
            <div className="chart-wrap" style={{ position: 'relative' }}>
              <svg width="100%" height="100%" viewBox="0 0 540 180" preserveAspectRatio="none">
                <line x1="0" y1="30" x2="540" y2="30" stroke="var(--line-2)" strokeDasharray="3 3" />
                <line x1="0" y1="80" x2="540" y2="80" stroke="var(--line-2)" strokeDasharray="3 3" />
                <line x1="0" y1="130" x2="540" y2="130" stroke="var(--line-2)" strokeDasharray="3 3" />

                <line
                  x1="0"
                  y1={180 - (60 / maxChartVal) * 160}
                  x2="540"
                  y2={180 - (60 / maxChartVal) * 160}
                  stroke="var(--amber)"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                />

                {chartDays.map((d, i) => {
                  const barWidth = 24;
                  const gap = (540 - 14 * barWidth) / 15;
                  const x = gap + i * (barWidth + gap);
                  const barHeight = (d.val / maxChartVal) * 150;
                  const y = 160 - barHeight;

                  return (
                    <g key={d.day + i}>
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={barHeight}
                        rx="4"
                        fill={d.isToday ? 'var(--teal-500)' : d.val >= d.goal ? 'var(--teal-800)' : 'rgba(15,76,92,0.65)'}
                        style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({ x: rect.left + rect.width / 2, y: rect.top, data: d });
                        }}
                        onMouseLeave={() => setTooltip(null)}
                      />
                      <text x={x + barWidth / 2} y="176" textAnchor="middle" fontSize="9" fill="var(--text-3)" fontFamily="JetBrains Mono">
                        {d.day.slice(0, 2)}
                      </text>
                    </g>
                  );
                })}
              </svg>

              {tooltip && (
                <div className="chart-tooltip show" style={{ position: 'fixed', left: `${tooltip.x}px`, top: `${tooltip.y - 12}px` }}>
                  <div className="ct-date">{tooltip.data.day}</div>
                  <div className="ct-val">{tooltip.data.val} <span className="u">entregas</span></div>
                  <div className="ct-note">{tooltip.data.val >= tooltip.data.goal ? '✓ Acima da meta' : 'Abaixo da meta (60)'}</div>
                </div>
              )}
            </div>

            <div className="legend">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="swatch" style={{ background: 'var(--teal-800)' }}></span>
                Entregas concluídas
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="swatch" style={{ background: 'var(--teal-500)' }}></span>
                Hoje
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 16, height: 2, background: 'var(--amber)', display: 'inline-block' }}></span>
                Meta diária (60)
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Operação em tempo real</div>
              <div className="card-sub">Visualização das rotas ativas do dia</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/rotas')}>
              Ver todas
            </button>
          </div>
          <div className="card-body" style={{ padding: 14 }}>
            <div className="mini-map-wrap">
              <div ref={mapContainerRef} id="dash-mini-map"></div>
            </div>

            <div style={{ marginTop: 14 }}>
              {todayRoutes.length === 0 && (
                <p style={{ color: 'var(--text-3)', fontSize: 13, padding: '8px 4px' }}>Nenhuma rota programada para hoje.</p>
              )}
              {todayRoutes.slice(0, 3).map((r) => (
                <div key={r.id} className="recent-route-item" onClick={() => navigate(`/rotas/${r.id}`)}>
                  <div>
                    <div className="rr-name">{r.name}</div>
                    <div className="rr-meta">
                      <span>{r.vehicle?.name || 'Veículo'}</span>
                      <span className="sep"></span>
                      <span>{r.vehicle?.driverName || '—'}</span>
                      <span className="sep"></span>
                      <span
                        className={`badge ${r.status === 'em_andamento' ? 'b-amber' : r.status === 'concluida' ? 'b-teal' : 'b-gray'}`}
                        style={{ fontSize: 10, padding: '1px 6px' }}
                      >
                        {r.status === 'em_andamento' ? 'Em andamento' : r.status === 'concluida' ? 'Concluída' : 'Planejada'}
                      </span>
                    </div>
                  </div>
                  <div className="rr-val">
                    <div>{fmtKm(r.distanceM / 1000)} <span className="u">km</span></div>
                    <div style={{ color: 'var(--text-3)', fontSize: 11, fontWeight: 400 }}>{fmtMin(r.durationS / 60)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Routes Table */}
      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Rotas recentes</div>
            <div className="card-sub">Últimas rotas calculadas e seus status de execução</div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/rotas')}>
            Ver todas as rotas ({routes.length})
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data">
            <thead>
              <tr>
                <th>Nome da rota</th>
                <th>Veículo</th>
                <th>Motorista</th>
                <th>Status</th>
                <th className="right">Paradas</th>
                <th className="right">Distância</th>
                <th className="right">Duração</th>
                <th className="right">Economia</th>
                <th className="right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {routes.slice(0, 5).map((r) => (
                <tr key={r.id}>
                  <td>
                    <a href={`#/rotas/${r.id}`} onClick={(e) => { e.preventDefault(); navigate(`/rotas/${r.id}`); }} style={{ color: 'var(--teal-800)', fontWeight: 600 }}>
                      {r.name}
                    </a>
                    <div style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 2 }}>{formatDate(r.date)}</div>
                  </td>
                  <td>
                    <span className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>{r.vehicle?.name || 'Veículo'}</span>
                    <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{r.vehicle?.plate || '—'}</div>
                  </td>
                  <td>{r.vehicle?.driverName || '—'}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.status === 'em_andamento' ? 'b-amber' : r.status === 'concluida' ? 'b-teal' : r.status === 'cancelada' ? 'b-danger' : 'b-gray'
                      }`}
                    >
                      <span className="dot"></span>
                      {r.status === 'em_andamento' ? 'Em andamento' : r.status === 'concluida' ? 'Concluída' : r.status === 'cancelada' ? 'Cancelada' : 'Planejada'}
                    </span>
                  </td>
                  <td className="right">{r.stops?.length || 0}</td>
                  <td className="right">{fmtKm(r.distanceM / 1000)} km</td>
                  <td className="right">{fmtMin(r.durationS / 60)}</td>
                  <td className="right">
                    <span style={{ color: r.savingsPercent > 0 ? '#0d7a6c' : 'var(--text-3)', fontWeight: 600 }}>
                      {r.savingsPercent > 0 ? `-${r.savingsPercent.toFixed(1)}%` : '—'}
                    </span>
                  </td>
                  <td className="right">
                    <div className="row-actions">
                      <button className="row-btn" onClick={() => navigate(`/rotas/${r.id}`)} title="Ver detalhes">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {routes.length === 0 && (
                <tr>
                  <td colSpan={9}>
                    <div className="empty">
                      <h4>Nenhuma rota ainda</h4>
                      <p>Crie uma rota ou rode a Otimização de Frota para começar.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
